import { getFunctions, httpsCallable } from 'firebase/functions';

export type CompanyBrainHitSource = 'github' | 'crm' | 'activity' | 'live_index';
export type CompanyBrainSection = 'github' | 'crm' | 'activity' | 'live_index';

export type CompanyBrainHitKind =
  | 'skill'
  | 'data'
  | 'system'
  | 'meeting'
  | 'marketing'
  | 'radar'
  | 'sales_digest'
  | 'activity'
  | 'other';

export interface CompanyBrainSubject {
  type: 'lead' | 'deal' | 'topic';
  id: string | null;
  label: string;
}

export interface CompanyBrainHit {
  id: string;
  source: CompanyBrainHitSource;
  section: CompanyBrainSection;
  kind: CompanyBrainHitKind;
  title: string;
  path: string | null;
  location: string;
  locationUrl: string | null;
  excerpt: string;
  url: string | null;
  entityRef: { type: 'deal' | 'lead' | 'project'; id: string; label?: string } | null;
  capabilityId: string | null;
  updatedAt: string | null;
  score: number;
}

export interface SearchCompanyBrainResult {
  success: boolean;
  query: string;
  repo: string;
  count: number;
  subject: CompanyBrainSubject | null;
  hits: CompanyBrainHit[];
  bySection: Record<CompanyBrainSection, number>;
  sources: Partial<Record<CompanyBrainHitSource, number>>;
  warnings: string[];
}

export async function searchCompanyBrain(input: {
  query: string;
  subjectId?: string | null;
  subjectType?: 'lead' | 'deal' | 'topic';
  subjectLabel?: string;
}): Promise<SearchCompanyBrainResult> {
  const fn = httpsCallable<
    {
      query: string;
      subjectId?: string;
      subjectType?: 'lead' | 'deal' | 'topic';
      subjectLabel?: string;
    },
    SearchCompanyBrainResult
  >(getFunctions(undefined, 'us-central1'), 'searchCompanyBrain');
  const res = await fn({
    query: input.query,
    ...(input.subjectId ? { subjectId: input.subjectId } : {}),
    ...(input.subjectType ? { subjectType: input.subjectType } : {}),
    ...(input.subjectLabel ? { subjectLabel: input.subjectLabel } : {}),
  });
  return res.data;
}

export function sourceLabel(source: CompanyBrainHitSource): string {
  switch (source) {
    case 'github':
      return 'Company Brain';
    case 'crm':
      return 'CRM';
    case 'activity':
      return 'Recent retrieval';
    case 'live_index':
      return 'Live index';
    default:
      return source;
  }
}

export function sectionLabel(section: CompanyBrainSection): string {
  switch (section) {
    case 'github':
      return 'Company Brain (GitHub)';
    case 'crm':
      return 'In the CRM';
    case 'activity':
      return 'Recent agent retrievals';
    case 'live_index':
      return 'Live index';
    default:
      return section;
  }
}

export function kindLabel(kind: CompanyBrainHitKind): string {
  switch (kind) {
    case 'skill':
      return 'Skill';
    case 'data':
      return 'Data';
    case 'system':
      return 'System';
    case 'meeting':
      return 'Meeting';
    case 'marketing':
      return 'Marketing';
    case 'radar':
      return 'Radar';
    case 'sales_digest':
      return 'Win/loss';
    case 'activity':
      return 'Activity';
    default:
      return 'Asset';
  }
}

export function groupHitsBySection(
  hits: CompanyBrainHit[],
): Array<{ section: CompanyBrainSection; label: string; hits: CompanyBrainHit[] }> {
  const order: CompanyBrainSection[] = ['github', 'crm', 'activity', 'live_index'];
  return order
    .map(section => ({
      section,
      label: sectionLabel(section),
      hits: hits.filter(h => h.section === section),
    }))
    .filter(g => g.hits.length > 0);
}
